import { useMemo, useState } from "react";
import type { FormEvent } from "react";
import type {
  DepartmentOption,
  EmployeeDetails,
  EmployeeFormValues,
  RoleOption,
} from "../types";
import { Button } from "#/components/common/Button";
const DEFAULT_ROLE = "EMPLOYEE_SELF_SERVICE";
const blankValues: EmployeeFormValues = {
  employeeNumber: "",
  userId: "",
  firstName: "",
  middleName: "",
  lastName: "",
  dateOfBirth: "",
  gender: "",
  maritalStatus: "",
  nationalIdNumber: "",
  kraPin: "",
  nhifNumber: "",
  nssfNumber: "",
  personalEmail: "",
  workEmail: "",
  mobilePhone: "",
  alternativePhone: "",
  addressLine1: "",
  addressLine2: "",
  cityTown: "",
  country: "Kenya",
  hireDate: "",
  probationEndDate: "",
  terminationDate: "",
  contractEndDate: "",
  employmentStatus: "ACTIVE",
  employmentType: "PERMANENT",
  jobTitle: "",
  primaryDepartmentId: "",
  departmentIds: [],
  emergencyContactName: "",
  emergencyContactRelationship: "",
  emergencyContactPhone: "",
  systemAccessEnabled: false,
  businessCardEnabled: false,
  roleCodes: [DEFAULT_ROLE],
};
function uniqueRoles(roles?: string[]) {
  return Array.from(new Set([DEFAULT_ROLE, ...(roles ?? [])]));
}
function toFormValues(employee?: EmployeeDetails): EmployeeFormValues {
  if (!employee) return blankValues;

  return {
    ...blankValues,
    employeeNumber: employee.employeeNumber ?? "",
    userId: employee.user?.id ?? "",
    firstName: employee.firstName ?? "",
    middleName: employee.middleName ?? "",
    lastName: employee.lastName ?? "",
    dateOfBirth: employee.dateOfBirth ?? "",
    gender: employee.gender ?? "",
    maritalStatus: employee.maritalStatus ?? "",
    nationalIdNumber: employee.nationalIdNumber ?? "",
    kraPin: employee.kraPin ?? "",
    nhifNumber: employee.nhifNumber ?? "",
    nssfNumber: employee.nssfNumber ?? "",
    personalEmail: employee.personalEmail ?? "",
    workEmail: employee.workEmail ?? "",
    mobilePhone: employee.mobilePhone ?? "",
    alternativePhone: employee.alternativePhone ?? "",
    addressLine1: employee.addressLine1 ?? "",
    addressLine2: employee.addressLine2 ?? "",
    cityTown: employee.cityTown ?? "",
    country: employee.country ?? "Kenya",
    hireDate: employee.hireDate ?? "",
    probationEndDate: employee.probationEndDate ?? "",
    terminationDate: employee.terminationDate ?? "",
    contractEndDate: employee.contractEndDate ?? "",
    employmentStatus: employee.employmentStatus ?? "ACTIVE",
    employmentType: employee.employmentType ?? "PERMANENT",
    jobTitle: employee.jobTitle ?? "",
    primaryDepartmentId: employee.primaryDepartmentId ?? "",
    departmentIds: employee.departments?.map((d) => d.id) ?? [],
    emergencyContactName: employee.emergencyContactName ?? "",
    emergencyContactRelationship: employee.emergencyContactRelationship ?? "",
    emergencyContactPhone: employee.emergencyContactPhone ?? "",
    systemAccessEnabled: Boolean(employee.systemAccessEnabled),
    businessCardEnabled: Boolean(employee.businessCardEnabled),
    roleCodes: uniqueRoles(employee.roleCodes ?? employee.user?.roles),
  };
}

export function EmployeeForm({
  employee,
  departments,
  roles,
  loading,
  onSubmit,
}: {
  employee?: EmployeeDetails;
  departments: DepartmentOption[];
  roles: RoleOption[];
  loading?: boolean;
  onSubmit: (values: EmployeeFormValues) => void;
}) {
  const initialValues = useMemo(() => toFormValues(employee), [employee]);
  const [values, setValues] = useState<EmployeeFormValues>(initialValues);
  const [error, setError] = useState<string | null>(null);

  function update<K extends keyof EmployeeFormValues>(
    key: K,
    value: EmployeeFormValues[K],
  ) {
    setValues((current) => ({ ...current, [key]: value }));
  }
  function toggleRole(code: string) {
    if (code === DEFAULT_ROLE) return;

    setValues((current) => {
      const exists = current.roleCodes.includes(code);

      return {
        ...current,

        roleCodes: uniqueRoles(
          exists
            ? current.roleCodes.filter((role) => role !== code)
            : [...current.roleCodes, code],
        ),
      };
    });
  }

  function toggleDepartment(id: number) {
    setValues((current) => {
      const exists = current.departmentIds.includes(id);

      const nextIds = exists
        ? current.departmentIds.filter((item) => item !== id)
        : [...current.departmentIds, id];

      return {
        ...current,

        departmentIds: nextIds,

        primaryDepartmentId:
          current.primaryDepartmentId === id && exists
            ? ""
            : current.primaryDepartmentId,
      };
    });
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();

    setError(null);

    if (!values.employeeNumber.trim()) {
      return setError("Employee number is required.");
    }

    if (!values.firstName.trim() || !values.lastName.trim()) {
      return setError("First name and last name are required.");
    }

    if (values.systemAccessEnabled && !values.workEmail.trim()) {
      return setError(
        "Work email is required when system login access is enabled.",
      );
    }

    onSubmit({
      ...values,

      roleCodes: values.systemAccessEnabled
        ? uniqueRoles(values.roleCodes)
        : [],
    });
  }
  return (
    <form
      onSubmit={handleSubmit}
      className="space-y-6 rounded-3xl border border-slate-200 bg-white p-6 shadow-sm"
    >
      <Section title="Employee identity">
        <Field label="Employee number">
          <input
            className={inputClass}
            value={values.employeeNumber}
            onChange={(e) => update("employeeNumber", e.target.value)}
          />
        </Field>

        <Field label="First name">
          <input
            className={inputClass}
            value={values.firstName}
            onChange={(e) => update("firstName", e.target.value)}
          />
        </Field>

        <Field label="Middle name">
          <input
            className={inputClass}
            value={values.middleName}
            onChange={(e) => update("middleName", e.target.value)}
          />
        </Field>

        <Field label="Last name">
          <input
            className={inputClass}
            value={values.lastName}
            onChange={(e) => update("lastName", e.target.value)}
          />
        </Field>

        <Field label="Date of birth">
          <input
            type="date"
            className={inputClass}
            value={values.dateOfBirth}
            onChange={(e) => update("dateOfBirth", e.target.value)}
          />
        </Field>

        <Field label="Gender">
          <select
            className={inputClass}
            value={values.gender}
            onChange={(e) => update("gender", e.target.value)}
          >
            <option value="">Select gender</option>
            <option value="MALE">Male</option>
            <option value="FEMALE">Female</option>
            <option value="OTHER">Other</option>
          </select>
        </Field>

        <Field label="Marital status">
          <select
            className={inputClass}
            value={values.maritalStatus}
            onChange={(e) => update("maritalStatus", e.target.value)}
          >
            <option value="">Select marital status</option>
            <option value="SINGLE">Single</option>
            <option value="MARRIED">Married</option>
            <option value="DIVORCED">Divorced</option>
            <option value="WIDOWED">Widowed</option>
          </select>
        </Field>
      </Section>

      <Section title="Employment details">
        <Field label="Job title">
          <input
            className={inputClass}
            value={values.jobTitle}
            onChange={(e) => update("jobTitle", e.target.value)}
          />
        </Field>

        <Field label="Employment status">
          <select
            className={inputClass}
            value={values.employmentStatus}
            onChange={(e) => update("employmentStatus", e.target.value)}
          >
            {[
              "ACTIVE",
              "ON_PROBATION",
              "ON_LEAVE",
              "SUSPENDED",
              "TERMINATED",
              "RESIGNED",
            ].map((item) => (
              <option key={item} value={item}>
                {item.replaceAll("_", " ")}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Employment type">
          <select
            className={inputClass}
            value={values.employmentType}
            onChange={(e) => update("employmentType", e.target.value)}
          >
            {[
              "PERMANENT",
              "CONTRACT",
              "INTERN",
              "TEMPORARY",
              "CONSULTANT",
              "CASUAL",
              "OTHER",
            ].map((item) => (
              <option key={item} value={item}>
                {item.replaceAll("_", " ")}
              </option>
            ))}
          </select>
        </Field>

        <Field label="Primary department">
          <select
            className={inputClass}
            value={values.primaryDepartmentId}
            onChange={(e) => {
              const id = e.target.value ? Number(e.target.value) : "";
              update("primaryDepartmentId", id);

              if (id) {
                setValues((current) => ({
                  ...current,
                  primaryDepartmentId: id,
                  departmentIds: Array.from(
                    new Set([...current.departmentIds, id]),
                  ),
                }));
              }
            }}
          >
            <option value="">Select primary department</option>
            {departments.map((department) => (
              <option key={department.id} value={department.id}>
                {department.name}
              </option>
            ))}
          </select>
        </Field>
        <div className="sm:col-span-2 lg:col-span-4">
          <p className="mb-2 text-sm font-medium text-slate-700">
            Other departments
          </p>

          <div className="grid gap-2 rounded-2xl border border-slate-200 p-4 sm:grid-cols-2 lg:grid-cols-3">
            {departments.map((department) => (
              <label
                key={department.id}
                className="flex items-center gap-2 text-sm text-slate-700"
              >
                <input
                  type="checkbox"
                  checked={values.departmentIds.includes(department.id)}
                  onChange={() => toggleDepartment(department.id)}
                />
                {department.name}
              </label>
            ))}
          </div>
        </div>

        <Field label="Hire date">
          <input
            type="date"
            className={inputClass}
            value={values.hireDate}
            onChange={(e) => update("hireDate", e.target.value)}
          />
        </Field>

        <Field label="Probation end date">
          <input
            type="date"
            className={inputClass}
            value={values.probationEndDate}
            onChange={(e) => update("probationEndDate", e.target.value)}
          />
        </Field>

        <Field label="Contract end date (interns / fixed-term staff)">
          <input
            type="date"
            className={inputClass}
            value={values.contractEndDate}
            onChange={(e) => update("contractEndDate", e.target.value)}
          />
        </Field>
      </Section>

      <Section title="Access and roles">
        <label className="flex items-center gap-3 rounded-2xl border border-slate-200 p-4 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={values.systemAccessEnabled}
            onChange={(e) => {
              update("systemAccessEnabled", e.target.checked);

              if (e.target.checked) {
                update("roleCodes", uniqueRoles(values.roleCodes));
              }
            }}
          />
          Allow system login access
        </label>

        <label className="flex items-center gap-3 rounded-2xl border border-slate-200 p-4 text-sm text-slate-700">
          <input
            type="checkbox"
            checked={values.businessCardEnabled}
            onChange={(e) => update("businessCardEnabled", e.target.checked)}
          />
          Enable digital business card
        </label>

        {values.systemAccessEnabled ? (
          <div className="sm:col-span-2 lg:col-span-4">
            <p className="mb-2 text-sm font-medium text-slate-700">
              Assigned roles
            </p>

            <div className="grid gap-2 rounded-2xl border border-slate-200 p-4 sm:grid-cols-2 lg:grid-cols-3">
              {roles.map((role) => {
                const checked = values.roleCodes.includes(role.code);

                const isDefault = role.code === DEFAULT_ROLE;

                return (
                  <label
                    key={role.id}
                    className={[
                      "flex items-start gap-2 rounded-xl border p-3 text-sm",

                      checked
                        ? "border-emerald-200 bg-emerald-50 text-emerald-800"
                        : "border-slate-200 text-slate-700",

                      !role.active ? "opacity-50" : "",
                    ].join(" ")}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={isDefault || !role.active}
                      onChange={() => toggleRole(role.code)}
                      className="mt-1"
                    />

                    <span>
                      <span className="block font-semibold">{role.name}</span>

                      <span className="block text-xs text-slate-500">
                        {role.code}

                        {isDefault ? " • default" : ""}
                      </span>
                    </span>
                  </label>
                );
              })}
            </div>

            <p className="mt-2 text-xs text-slate-500">
              Employee Self Service is always assigned automatically.
            </p>
          </div>
        ) : null}
      </Section>
      <Section title="Contacts">
        <Field label="Work email">
          <input
            type="email"
            className={inputClass}
            value={values.workEmail}
            onChange={(e) => update("workEmail", e.target.value)}
          />
        </Field>

        <Field label="Personal email">
          <input
            type="email"
            className={inputClass}
            value={values.personalEmail}
            onChange={(e) => update("personalEmail", e.target.value)}
          />
        </Field>

        <Field label="Mobile phone">
          <input
            className={inputClass}
            value={values.mobilePhone}
            onChange={(e) => update("mobilePhone", e.target.value)}
          />
        </Field>

        <Field label="Alternative phone">
          <input
            className={inputClass}
            value={values.alternativePhone}
            onChange={(e) => update("alternativePhone", e.target.value)}
          />
        </Field>
      </Section>

      <Section title="Statutory details">
        <Field label="National ID">
          <input
            className={inputClass}
            value={values.nationalIdNumber}
            onChange={(e) => update("nationalIdNumber", e.target.value)}
          />
        </Field>

        <Field label="KRA PIN">
          <input
            className={inputClass}
            value={values.kraPin}
            onChange={(e) => update("kraPin", e.target.value)}
          />
        </Field>

        <Field label="SHIF number">
          <input
            className={inputClass}
            value={values.nhifNumber}
            onChange={(e) => update("nhifNumber", e.target.value)}
          />
        </Field>

        <Field label="NSSF number">
          <input
            className={inputClass}
            value={values.nssfNumber}
            onChange={(e) => update("nssfNumber", e.target.value)}
          />
        </Field>
      </Section>

      <Section title="Emergency contact">
        <Field label="Name">
          <input
            className={inputClass}
            value={values.emergencyContactName}
            onChange={(e) => update("emergencyContactName", e.target.value)}
          />
        </Field>

        <Field label="Relationship">
          <input
            className={inputClass}
            value={values.emergencyContactRelationship}
            onChange={(e) =>
              update("emergencyContactRelationship", e.target.value)
            }
          />
        </Field>

        <Field label="Phone">
          <input
            className={inputClass}
            value={values.emergencyContactPhone}
            onChange={(e) => update("emergencyContactPhone", e.target.value)}
          />
        </Field>
      </Section>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      ) : null}

      <div className="flex justify-end border-t border-slate-200 pt-5">
        <Button type="submit" loading={loading}>
          Save employee
        </Button>
      </div>
    </form>
  );
}

const inputClass =
  "w-full rounded-2xl border border-slate-300 px-4 py-3 text-sm outline-none transition focus:border-emerald-500 focus:ring-2 focus:ring-emerald-100";

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section>
      <h2 className="mb-4 text-sm font-bold uppercase tracking-wide text-slate-500">
        {title}
      </h2>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">{children}</div>
    </section>
  );
}

function Field({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="space-y-2">
      <span className="text-sm font-medium text-slate-700">{label}</span>
      {children}
    </label>
  );
}
